"use client";

/**
 * Impersonation engine — nav-pin guard.
 *
 * Pins the browser to the active layer's route tree: when the user strays OUT of it
 * (browser Back/Forward into a route we've already left, or a stray link), SNAP them back
 * to where the active identity lives — keeping the impersonation stack intact. The only
 * way to unwind a layer is the explicit Exit control. A single `pathname` effect covers
 * Back and Forward, because `usePathname()` updates on `popstate` too in the App Router —
 * one watcher avoids the double-fire risk of a separate `popstate` listener.
 *
 * `armedRef` lives in the provider (not here) because `impersonate()` / `exit()` must
 * DISARM it the moment they kick off a navigation — otherwise this effect, seeing the
 * new top layer while `pathname` is still the page we left from, would snap back spuriously
 * and fight the programmatic navigation. We only snap back once we've confirmed we were
 * armed (i.e. genuinely sitting inside the tree) and then strayed out.
 */
import { useEffect, type RefObject } from "react";
import { useRouter } from "next/navigation";
import { ROLE_REGISTRY } from "./registry";
import type {
  ImpersonationKind,
  ImpersonationState,
  ImpersonationTarget,
} from "./types";

/** Top-of-stack target + its registry spec. */
const topOf = (
  s: NonNullable<ImpersonationState>,
): {
  target: ImpersonationTarget;
  spec: (typeof ROLE_REGISTRY)[ImpersonationKind];
} => {
  const target = s.stack[s.stack.length - 1];
  return { target, spec: ROLE_REGISTRY[target.kind] };
};

interface NavExitGuardArgs {
  state: ImpersonationState;
  pathname: string;
  /** Shared with the provider so it can disarm before a programmatic navigation. */
  armedRef: RefObject<boolean>;
}

const matchesPrefix = (pathname: string, prefix: string | string[]): boolean =>
  Array.isArray(prefix)
    ? prefix.some((p) => pathname.startsWith(p))
    : pathname.startsWith(prefix);

export function useNavExitGuard({
  state,
  pathname,
  armedRef,
}: NavExitGuardArgs): void {
  const router = useRouter();
  useEffect(() => {
    if (!state) {
      armedRef.current = false;
      return;
    }
    const { target, spec } = topOf(state);
    const prefix = spec.routePrefix(target as never);
    if (matchesPrefix(pathname, prefix)) {
      armedRef.current = true; // inside the active tree → arm
    } else if (armedRef.current) {
      // Armed and now outside the active tree (e.g. browser Back into a route we've
      // already exited). Keep the stack intact and snap back to where this identity
      // lives — only the Exit control pops a layer.
      router.replace(spec.entryRoute(target as never));
    }
  }, [pathname, state, armedRef, router]);
}
